import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

export const getInvoices = async (params = {}) => {
  const response = await axiosInstance.get('Invoices/get-invoices', { params });
  const data = response.data;
  
  // Handle response structure: { status, payload, message, errorCode }
  if (data.status === 'Success' && data.payload) {
    return data.payload;
  }
  
  // Return empty array if no payload or error
  return [];
};

export const getInvoiceById = async (id) => {
  const response = await axiosInstance.get(`Invoices/get-invoice/${id}`);
  const data = response.data;
  
  // Handle response structure
  if (data.status === 'Success' && data.payload) {
    return data.payload;
  }
  
  // Handle error response
  if (data.status === 'Failure') {
    throw new Error(data.message || 'Invoice not found');
  }
  
  return null;
};

export const addInvoice = async (invoice, isFormData = false) => {
  let config = {};
  
  // If it's FormData, configure axios to send it properly
  if (isFormData && invoice instanceof FormData) {
    config = {
      headers: {
        'Content-Type': undefined, // Let browser set multipart/form-data with boundary
      },
      transformRequest: [(data) => data], // Prevent axios from transforming FormData
    };
  }
  
  const response = await axiosInstance.post('Invoices/add-invoice', invoice, config);
  const data = response.data;
  
  // Handle response structure
  if (data.status === 'Success') {
    return data.payload || data;
  }
  
  // Handle error response
  if (data.status === 'Failure') {
    throw new Error(data.message || 'Failed to create invoice');
  }
  
  return data;
};

export const updateInvoice = async (id, invoice, isFormData = false) => {
  let config = {};
  if (isFormData && invoice instanceof FormData) {
    config = {
      headers: {
        'Content-Type': undefined, // Let browser set multipart/form-data with boundary
      },
      transformRequest: [(data) => data], // Prevent axios from transforming FormData
    };
  }
  const response = await axiosInstance.put(`Invoices/update-invoice/${id}`, invoice, config);
  const data = response.data;
  
  // Handle response structure
  if (data.status === 'Success') {
    return data.payload || data;
  }
  
  // Handle error response
  if (data.status === 'Failure') {
    throw new Error(data.message || 'Failed to update invoice');
  }
  
  return data;
};

export const deleteInvoice = async (id) => {
  await axiosInstance.delete(`Invoices/delete-invoice/${id}`);
};

export const getInvoicesByCustomer = async (customerId) => {
  const response = await axiosInstance.get(`Invoices/invoices-by-customer/${customerId}`);
  const data = response.data;
  
  // Handle response structure
  if (data.status === 'Success' && data.payload) {
    return data.payload;
  }
  
  return [];
};

export const getInvoicesByProduct = async (productId) => {
  const response = await axiosInstance.get(`Invoices/invoices-by-product/${productId}`);
  return response.data;
};

export const getOutstandingInvoicesByCustomer = async (customerId) => {
  const res = await axiosInstance.get(`/Invoices/outstanding-invoices-by-customer/${customerId}`);
  const data = res.data;
  
  // Handle response structure
  if (data.status === 'Success' && data.payload) {
    return data.payload;
  }
  
  return [];
};

// Peppol Invoice APIs
export const getPendingPeppolInvoices = async () => {
  const response = await axiosInstance.get('Peppol/get-stored-pending-messages');
  return response.data;
};

export const getDeliveredPeppolInvoices = async () => {
  const response = await axiosInstance.get('Peppol/get-stored-delivered-messages');
  return response.data;
};

export const getPeppolInvoiceByUniqueId = async (uniqueId) => {
  const response = await axiosInstance.get(`Peppol/inbound-invoice/${uniqueId}`);
  return response.data;
};

export const markPeppolInvoiceAsDelivered = async (uniqueId) => {
  const response = await axiosInstance.post(`Peppol/mark-as-delivered/${uniqueId}`);
  return response.data;
};

export const sendInvoiceToPeppol = async (formData) => {
  const response = await axiosInstance.post('Invoices/send-to-peppol', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = response.data;
  
  if (data.status === 'Success') {
    return data.payload || data;
  }
  
  throw new Error(data.message || 'Failed to send invoice to Peppol');
};

export const sendInvoiceByEmail = async (formData) => {
  const response = await axiosInstance.post('Invoices/send-by-email', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = response.data;
  
  if (data.status === 'Success') {
    return data.payload || data;
  }
  
  throw new Error(data.message || 'Failed to send invoice by email');
};

export const checkNewPeppolInvoices = async () => {
  const response = await axiosInstance.get('Peppol/pending-inbound');
  return response.data;
};

export const syncDeliveredInvoices = async () => {
  const response = await axiosInstance.get('Peppol/sync-delivered-invoices');
  return response.data;
};

export const downloadPeppolAttachment = async (uniqueId) => {
  const response = await axiosInstance.get(`Peppol/download-attachment/${uniqueId}`, {
    responseType: 'blob',
  });
  return response;
};

export const updatePeppolInvoicePaymentStatus = async (data) => {
  const response = await axiosInstance.post('Peppol/update-invoice-payment', data);
  return response.data;
};

export const getNextInvoiceNumber = async () => {
  const response = await axiosInstance.get('Invoices/next-invoice-number');
  const data = response.data;
  
  // Handle response structure
  if (data.status === 'Success' && data.payload) {
    return data.payload;
  }
  
  // Return the data directly if it's a string (backward compatibility)
  if (typeof data === 'string') {
    return data;
  }
  
  return null;
};

export const recordInvoicePayment = async (paymentData) => {
  const response = await axiosInstance.post('Invoices/record-payment', paymentData);
  const data = response.data;
  
  // Handle response structure
  if (data.status === 'Success') {
    return data.payload || data;
  }
  
  // Handle error response
  if (data.status === 'Failure') {
    throw new Error(data.message || 'Failed to record payment');
  }
  
  return data;
};

export default {
  getInvoices,
  getInvoiceById,
  addInvoice,
  updateInvoice,
  deleteInvoice,
  getInvoicesByCustomer,
  getInvoicesByProduct,
  getOutstandingInvoicesByCustomer,
  getPendingPeppolInvoices,
  getDeliveredPeppolInvoices,
  getPeppolInvoiceByUniqueId,
  markPeppolInvoiceAsDelivered,
  sendInvoiceToPeppol,
  sendInvoiceByEmail,
  checkNewPeppolInvoices,
  syncDeliveredInvoices,
  downloadPeppolAttachment,
  updatePeppolInvoicePaymentStatus,
  getNextInvoiceNumber,
  recordInvoicePayment
};